import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";

/**
 * Fixed top-left sidebar language switcher. Visible site-wide.
 * Hover/click → opens a dropdown listing all 8 supported languages.
 */
export function LanguageSwitcher() {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const active = SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.language || "en").split("-")[0]) || SUPPORTED_LANGUAGES[0];

    return (
        <div
            ref={ref}
            className="fixed left-3 sm:left-4 top-20 z-[60]"
            data-testid="language-switcher"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 backdrop-blur border border-orange-100 shadow-lg hover:border-[var(--medx-orange)] transition-all text-sm font-medium text-gray-800"
                data-testid="language-switcher-toggle"
                aria-label={t("common.language")}
            >
                <Globe className="w-4 h-4 text-[var(--medx-red)]" />
                <span className="hidden sm:inline">{active.flag} {active.label}</span>
                <span className="sm:hidden">{active.flag}</span>
            </button>

            {open && (
                <div
                    className="absolute left-0 mt-2 w-56 rounded-2xl bg-white shadow-2xl border border-orange-100 overflow-hidden"
                    data-testid="language-switcher-menu"
                >
                    <div className="px-4 py-2 text-[10px] font-mono tracking-[0.2em] text-gray-400 border-b border-gray-100">
                        {t("common.language").toUpperCase()}
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => {
                        const selected = lang.code === active.code;
                        return (
                            <button
                                key={lang.code}
                                onClick={() => {
                                    i18n.changeLanguage(lang.code);
                                    setOpen(false);
                                }}
                                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors ${
                                    selected ? "bg-orange-50/80 text-[var(--medx-red)] font-semibold" : "text-gray-700"
                                }`}
                                data-testid={`language-option-${lang.code}`}
                            >
                                <span className="flex items-center gap-2">
                                    <span className="text-base">{lang.flag}</span> {lang.label}
                                </span>
                                {selected && <Check className="w-4 h-4 text-[var(--medx-red)]" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
